import { Badge } from '@/components/Badge'
import { Card, CardBody, CardHeader } from '@/components/Card'
import { RefreshButton } from '@/components/RefreshButton'
import { EmptyState } from '@/components/EmptyState'
import { ErrorNotice } from '@/components/ErrorNotice'
import { ProgressBar } from '@/components/ProgressBar'
import { SkeletonRows } from '@/components/Skeleton'
import { StaleNotice } from '@/components/StaleNotice'
import { useDownloads } from '@/hooks/useRequests'
import { formatBytes, formatEta } from '@/lib/format'
import type { DownloadItem } from '@/lib/types'

/** Bytes per second, sized to the value so a slow torrent doesn't read "0 MB/s". */
function formatRate(bytesPerSecond: number): string {
  if (!bytesPerSecond) return '0 B/s'
  return `${formatBytes(bytesPerSecond)}/s`
}

/** Compact duration for "active for" — hours and minutes, never seconds. */
function formatActive(seconds: number): string | null {
  if (!seconds || seconds < 60) return null
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  return hours ? `${hours}h ${minutes}m` : `${minutes}m`
}

/**
 * One label/value pair. The label is signage, the value monospace, so a column
 * of these stays aligned as numbers change under polling.
 */
function Stat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="min-w-0">
      <dt className="signage text-[9px] text-faint">{label}</dt>
      <dd className={`font-mono text-[11px] tabular-nums ${tone ?? 'text-muted'}`}>{value}</dd>
    </div>
  )
}

/*
 * qBittorrent knows things the *arrs do not: live transfer rates, how healthy
 * the swarm is, and what has actually been written to disk. Those are exactly
 * the numbers you want when a download is behaving badly, so they get their own
 * stat grid rather than being crushed into the progress bar's detail line.
 */
function TorrentStats({ torrent }: { torrent: NonNullable<DownloadItem['torrent']> }) {
  const active = formatActive(torrent.timeActive)
  // Availability below 1.0 means no complete copy is reachable — the download
  // cannot finish until a seed appears, which is worth flagging rather than
  // leaving as a bare number.
  const starved = torrent.availability > 0 && torrent.availability < 1

  return (
    <dl className="mt-3 grid grid-cols-3 gap-x-3 gap-y-2.5 border-t border-line pt-3 sm:grid-cols-6">
      <Stat
        label="Down"
        value={formatRate(torrent.dlspeed)}
        tone={torrent.dlspeed > 0 ? 'text-accent' : 'text-faint'}
      />
      <Stat label="Up" value={formatRate(torrent.upspeed)} />
      <Stat
        label="Seeds"
        value={`${torrent.numSeeds}/${torrent.numComplete}`}
        tone={torrent.numSeeds === 0 ? 'text-danger' : 'text-muted'}
      />
      <Stat label="Peers" value={`${torrent.numLeechs}/${torrent.numIncomplete}`} />
      <Stat
        label="Avail"
        value={torrent.availability.toFixed(1)}
        tone={starved ? 'text-warn' : 'text-muted'}
      />
      <Stat label="Ratio" value={torrent.ratio.toFixed(2)} />

      <Stat label="Got" value={formatBytes(torrent.downloaded)} />
      <Stat label="Sent" value={formatBytes(torrent.uploaded)} />
      <Stat label="State" value={torrent.state} />
      {torrent.trackerHost ? <Stat label="Tracker" value={torrent.trackerHost} /> : null}
      {active ? <Stat label="Active" value={active} /> : null}
      {torrent.category ? <Stat label="Category" value={torrent.category} /> : null}
    </dl>
  )
}

/*
 * The queue is a board, not a spreadsheet: every row carries a live progress
 * bar, so it stays a stacked row at every width rather than becoming a table
 * that a phone would have to scroll sideways.
 */
export function AdminDownloadsPage() {
  const downloads = useDownloads()
  const items = downloads.data?.items ?? []
  const sources = downloads.data?.sources

  return (
    <Card>
      <CardHeader
        title={`Download queue${items.length ? ` (${items.length})` : ''}`}
        description="Radarr and Sonarr queues, enriched with live torrent state from qBittorrent. Refreshes every 10 seconds while this tab is open."
        action={<RefreshButton />}
      />
      <CardBody flush>
        {downloads.data?.stale ? (
          <div className="border-b border-line px-4 py-3">
            <StaleNotice />
          </div>
        ) : null}

        {/* Say which source is missing rather than silently showing less data. */}
        {sources && sources.qbittorrent === false ? (
          <p className="border-b border-line px-4 py-2.5 text-xs text-warn">
            qBittorrent is unreachable, so transfer rates and swarm health are unavailable. Queue
            positions below still come from Radarr and Sonarr.
          </p>
        ) : null}

        {downloads.isPending ? (
          <div className="p-4">
            <SkeletonRows rows={5} />
          </div>
        ) : downloads.isError && items.length === 0 ? (
          <div className="p-4">
            <ErrorNotice error={downloads.error} onRetry={() => void downloads.refetch()} />
          </div>
        ) : items.length === 0 ? (
          <div className="p-4">
            <EmptyState
              title="The queue is empty"
              description="Nothing is downloading right now — everything requested has landed."
            />
          </div>
        ) : (
          items.map((item) => {
            // Prefer qBittorrent's ETA: it is recomputed continuously, while the
            // *arr figure only refreshes when its own queue is polled.
            const eta = formatEta(item.torrent?.eta ?? item.eta)
            return (
              <div key={item.id} className="board-row px-4 py-3.5">
                <div className="flex items-start justify-between gap-2">
                  <p className="signage-tight min-w-0 text-sm text-ink">{item.title}</p>
                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                    {item.service ? <Badge>{item.service}</Badge> : null}
                    {item.errorMessage ? <Badge tone="danger">Error</Badge> : null}
                  </div>
                </div>

                {item.username ? (
                  <p className="mt-1 flex items-baseline gap-1.5">
                    <span className="signage text-[9px] text-faint">For</span>
                    <span className="font-mono text-[11px] text-muted">{item.username}</span>
                  </p>
                ) : null}

                <div className="mt-2.5">
                  <ProgressBar
                    value={item.progress}
                    label={item.status || 'Downloading'}
                    detail={[
                      eta ? `${eta} left` : null,
                      item.sizeLeft > 0
                        ? `${formatBytes(item.sizeLeft)} of ${formatBytes(item.size)} left`
                        : null,
                      item.client,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  />
                </div>

                {item.torrent ? <TorrentStats torrent={item.torrent} /> : null}

                {item.errorMessage ? (
                  <p className="mt-2 text-xs text-danger">{item.errorMessage}</p>
                ) : null}
              </div>
            )
          })
        )}
      </CardBody>
    </Card>
  )
}
